<template>
  <div class="api-docs">
    <UiCollapsible title="Api Docs" ref="collapsible">
      <UiTabs @tab-change="updateHeight">
        <UiTab title="Reports">
          <UiTabs @tab-change="updateHeight">
            <UiTab title="List Reports">
              <ApiPanel
                endpoint="/api/reports"
                :methods="['GET']"
                :token="token"
                @updateHeight="updateHeight"
              />
            </UiTab>
            <UiTab title="Report Detail">
              <ApiPanel
                :endpoint="`/api/reports/${reportId}`"
                :methods="['GET']"
                :token="token"
                @updateHeight="updateHeight"
              >
                <div slot="endpoint" style="display:flex">
                  /api/reports/
                  <UiTextbox
                    v-model="reportId"
                    placeholder="<report-id>"
                    style="width:105px"
                  />
                </div>
              </ApiPanel>
            </UiTab>
          </UiTabs>
        </UiTab>
        <UiTab title="Users">
          <UiTabs @tab-change="updateHeight">
            <UiTab title="List Users">
              <ApiPanel
                endpoint="/api/users"
                :methods="['GET']"
                :token="token"
                @updateHeight="updateHeight"
              />
            </UiTab>
            <UiTab
              title="Create Users"
              @select="updateUserBodyHeight"
            >
              <ApiPanel
                endpoint="/api/users"
                :methods="['POST']"
                :token="token"
                :body=usersJson
                @updateHeight="updateHeight"
              >
                <div slot="body">
                  <UiTextbox
                    ref="userBody"
                    @change="updateHeight"
                    v-model="usersPayload"
                    :multi-line="true"
                    :spellcheck="false"
                    :autocomplete="'off'"
                    :autocapitalize="'off'"
                    :autocorrect="'off'"
                    label="body"
                  />
                </div>
              </ApiPanel>
            </UiTab>
          </UiTabs>>
        </UiTab>
        <UiTab title="Webhooks">
          <UiTabs
            @tab-change="updateHeight"
            ref="webhookTabList"
          >
            <UiTab title="Create Webhooks">
              <ApiPanel
                endpoint="/api/webhooks"
                :methods="['POST']"
                :token="token"
                :body="{ endpoint: webhookEndpoint }"
                @updateHeight="updateHeight"
              >
                <div slot="body">
                  <UiTextbox
                    v-model="webhookEndpoint"
                    label="endpoint"
                  />
                </div>
                <div slot="description">
                  <p>
                    Webhooks enable you to receive real-time updates when a new Report is created.
                    When a new report is created, every endpoint you specify will recieve an HTTP
                    POST with the report detail.
                  </p>
                  <p>
                    If you'd like to try out webhooks without standing up your own server,
                    we recommend using
                    <a href="https://requestb.in/">RequestBin</a> or
                    <a href="https://hookbin.com/">Hookbin</a>
                    to set up an endpoint, and the
                    <a href="#" @click="$refs.webhookTabList.setActiveTab('test')">Test Webhook</a>
                    action to send a webhook for a pre-existing report.
                  </p>
                  <p>
                    Since making a webhook involves creating an open-to-the-world endpoint on your
                    server, there is some security risk involved. In order to mitigate this, Every
                    webhook will be given a unique token (shown when it is first created, and in the
                    <a href="#" @click="$refs.webhookTabList.setActiveTab('list')">List Webhooks</a>
                    response). The token will appear in the payload, and will also be passed as an
                    authorization header. By checking this value, an attacker cannot fool you into
                    falsly believing a new report was generated.
                  </p>
                  <p>
                    If the token is compromised, you can re-created it by making a
                    <a href="#" @click="$refs.webhookTabList.setActiveTab('regenerate')">
                      Regenerate Token
                    </a>
                    call.
                  </p>
                </div>
              </ApiPanel>
            </UiTab>
            <UiTab title="List Webhooks" id="list">
              <ApiPanel
                endpoint="/api/webhooks"
                :methods="['GET']"
                :token="token"
                @updateHeight="updateHeight"
              />
            </UiTab>
            <UiTab title="Delete Webhook">
              <ApiPanel
                :endpoint="`/api/webhooks/${webhookId}`"
                :methods="['DELETE']"
                :token="token"
                @updateHeight="updateHeight"
              >
                <div slot="endpoint" style="display:flex">
                  /api/webhooks/
                  <UiTextbox
                    v-model="webhookId"
                    placeholder="<webhook-id>"
                    style="width:128px"
                  />
                </div>
              </ApiPanel>
            </UiTab>
            <UiTab title="Test Webhooks" id="test">
              <ApiPanel
                :endpoint="`/api/reports/${reportId}/test_webhooks`"
                :methods="['POST']"
                :token="token"
                @updateHeight="updateHeight"
              >
                <div slot="endpoint" style="display:flex">
                  /api/reports/
                  <UiTextbox
                    v-model="reportId"
                    placeholder="<report-id>"
                    style="width:105px"
                  />
                  /test_webhooks
                </div>
              </ApiPanel>
            </UiTab>
            <UiTab title="Regenerate Token" id="regenerate">
              <ApiPanel
                :endpoint="`/api/webhooks/${webhookId}/regenerate_token`"
                :methods="['POST']"
                :token="token"
                @updateHeight="updateHeight"
              >
                <div slot="endpoint" style="display:flex">
                  /api/webhooks/
                  <UiTextbox
                    v-model="webhookId"
                    placeholder="<webhook-id>"
                    style="width:128px"
                  />
                  /regenerate_token
                </div>
              </ApiPanel>
            </UiTab>
          </UiTabs>
        </UiTab>
      </UiTabs>
    </UiCollapsible>
  </div>
</template>

<script>
import _ from 'lodash';
import { UiCollapsible, UiTabs, UiTab, UiTextbox } from 'keen-ui';
import ApiPanel from './ApiPanel';

export default {
  name: 'ApiDocs',
  props: {
    token: String,
    orgId: Number,
  },
  data() {
    return {
      reportId: null,
      usersPayload: `[
  {
    "custom_id": "snooze-ya-lose",
    "email": "beauregard@example.com",
    "first_name": "Beauregard",
    "last_name": "Snarflegrumps",
    "phone_number": "8675309",
    "password": "toast"
  }
]`,
      webhookEndpoint: '',
      webhookId: null,
    };
  },
  components: {
    UiCollapsible,
    UiTabs,
    UiTab,
    ApiPanel,
    UiTextbox,
  },
  computed: {
    usersJson() { return JSON.parse(this.usersPayload); },
  },
  methods: {
    updateHeight() {
      this.$refs.collapsible.refreshHeight();
    },
    updateUserBodyHeight() {
      _.defer(() => this.$refs.userBody.refreshSize());
      _.defer(() => this.updateHeight());
    },
  },
};
</script>

<style lang="scss">
</style>



// WEBPACK FOOTER //
// src/components/ApiDocs.vue